import type { Tagged } from 'type-fest';
import { dayjs } from '../../../shared/utils/date';
import { UniqueId } from '../UniqueId';
import type { Account } from '.';

const idGenerator = UniqueId.GetFactory(10);

export type Grant = {
  id: Grant.Id;
  accountId: Account.Id;
  grantKey: Grant.Key;
  kind: Grant.Kind;
  lifecycleState: Grant.LifecycleState;
  reason: string | undefined;
  expiresAt: dayjs | undefined;
  revokedAt: dayjs | undefined;
  revokedById: Account.Id | undefined;
  revokeReason: string | undefined;
  pamGrantName: string | undefined;
  pamEntitlementName: string | undefined;
  pamRequestId: string | undefined;
  requestedAt: dayjs | undefined;
  approvedAt: dayjs | undefined;
  activatedAt: dayjs | undefined;
  createdById: Account.Id;
  createdAt: dayjs;
  updatedById: Account.Id;
  updatedAt: dayjs;
};

export namespace Grant {
  export type Id = Tagged<UniqueId, 'accountGrantId'>;
  export namespace Id {
    export const Generate = () => idGenerator.generate() as Id;
    export const FromString = (id: string): Id => idGenerator.FromString(id) as Id;
  }

  export namespace Key {
    export const Employee = 'employee';
    export const SystemAdminEligible = 'system_admin.eligible';
    export const SystemAdminActive = 'system_admin.active';
    export const PiiAccessEligible = 'pii.access.eligible';
    export const PiiAccessActive = 'pii.access.active';

    export const Durable = [Employee, SystemAdminEligible, PiiAccessEligible] as const;
    export type Durable = (typeof Durable)[number];

    export const Ephemeral = [SystemAdminActive, PiiAccessActive] as const;
    export type Ephemeral = (typeof Ephemeral)[number];

    export const All = [...Durable, ...Ephemeral] as const;

    export const IsDurable = (grantKey: string): grantKey is Durable => Durable.includes(grantKey as Durable);
    export const IsEphemeral = (grantKey: string): grantKey is Ephemeral => Ephemeral.includes(grantKey as Ephemeral);
    export const IsGrant = (grantKey: string): grantKey is Grant.Key => IsDurable(grantKey) || IsEphemeral(grantKey);

    export const FromString = (grantKey: string): Grant.Key => {
      if (IsGrant(grantKey)) return grantKey;
      throw new Error(`Unknown account grant key: ${grantKey}`);
    };

    export const GetKind = (grantKey: Grant.Key): Kind => (IsDurable(grantKey) ? Kind.Durable : Kind.Ephemeral);
  }
  export type Key = Key.Durable | Key.Ephemeral;

  export namespace Kind {
    export const Durable = 'durable';
    export const Ephemeral = 'ephemeral';
  }
  export type Kind = (typeof Kind)[keyof typeof Kind];

  export namespace LifecycleState {
    export const Requested = 'requested';
    export const Approved = 'approved';
    export const Active = 'active';
    export const Revoked = 'revoked';
    export const Expired = 'expired';
  }
  export type LifecycleState = (typeof LifecycleState)[keyof typeof LifecycleState];

  export const DefaultActiveDurationMinutes = 30;

  export type Props = {
    id: string;
    accountId: string;
    grantKey: string;
    kind: string;
    lifecycleState: string;
    reason?: string | null | undefined;
    expiresAt?: string | Date | dayjs | null | undefined;
    revokedAt?: string | Date | dayjs | null | undefined;
    revokedById?: string | null | undefined;
    revokeReason?: string | null | undefined;
    pamGrantName?: string | null | undefined;
    pamEntitlementName?: string | null | undefined;
    pamRequestId?: string | null | undefined;
    requestedAt?: string | Date | dayjs | null | undefined;
    approvedAt?: string | Date | dayjs | null | undefined;
    activatedAt?: string | Date | dayjs | null | undefined;
    createdById: string;
    createdAt: string | Date | dayjs;
    updatedById: string;
    updatedAt: string | Date | dayjs;
  };

  const parseLifecycleState = (state: string): LifecycleState => {
    if (Object.values(LifecycleState).includes(state as LifecycleState)) return state as LifecycleState;
    throw new Error(`Unknown account grant lifecycle state: ${state}`);
  };

  const parseKind = (kind: string): Kind => {
    if (Object.values(Kind).includes(kind as Kind)) return kind as Kind;
    throw new Error(`Unknown account grant kind: ${kind}`);
  };

  const parseDate = (value: string | Date | dayjs | null | undefined): dayjs | undefined => (value ? dayjs(value) : undefined);

  export const Construct = (props: Props): Grant => {
    const grantKey = Key.FromString(props.grantKey);
    const kind = parseKind(props.kind);
    if (kind !== Key.GetKind(grantKey)) {
      throw new Error(`Grant kind mismatch: grantKey=${grantKey}, kind=${kind}`);
    }

    return {
      id: Id.FromString(props.id),
      accountId: props.accountId as Account.Id,
      grantKey,
      kind,
      lifecycleState: parseLifecycleState(props.lifecycleState),
      reason: props.reason ?? undefined,
      expiresAt: parseDate(props.expiresAt),
      revokedAt: parseDate(props.revokedAt),
      revokedById: props.revokedById ? (props.revokedById as Account.Id) : undefined,
      revokeReason: props.revokeReason ?? undefined,
      pamGrantName: props.pamGrantName ?? undefined,
      pamEntitlementName: props.pamEntitlementName ?? undefined,
      pamRequestId: props.pamRequestId ?? undefined,
      requestedAt: parseDate(props.requestedAt),
      approvedAt: parseDate(props.approvedAt),
      activatedAt: parseDate(props.activatedAt),
      createdById: props.createdById as Account.Id,
      createdAt: dayjs(props.createdAt),
      updatedById: props.updatedById as Account.Id,
      updatedAt: dayjs(props.updatedAt),
    };
  };

  export const IsCurrentlyEffective = (params: { grant: Grant; now?: dayjs }): boolean => {
    const { grant, now = dayjs(new Date()) } = params;
    if (grant.revokedAt) return false;
    if (grant.lifecycleState === LifecycleState.Revoked || grant.lifecycleState === LifecycleState.Expired) return false;
    if (grant.lifecycleState !== LifecycleState.Active) return false;
    if (grant.kind === Kind.Durable) return true;
    return !!grant.expiresAt && grant.expiresAt.isAfter(now);
  };

  /**
   * アカウントグラントを解釈するための標準ヘルパー。
   *
   * グラントが有効かどうかを判定するコードでは、このヘルパーか、
   * ここから生成された AuthInfo の boolean を参照すること。
   * 利用側アプリで生の AccountGrant 行を直接参照しないこと。生の行には
   * 申請中、取り消し済み、期限切れの履歴も含まれ、権限判定の source of truth ではない。
   */
  export namespace Effective {
    export type Ephemeral = {
      id: Id;
      grantKey: Key.Ephemeral;
      expiresAt: dayjs;
      reason?: string | undefined;
    };

    export type Props = {
      durable: Key.Durable[];
      ephemeral: Ephemeral[];
    };

    export const Empty = (): Props => ({ durable: [], ephemeral: [] });

    /**
     * 生のグラント行から有効なグラント集合を生成する。
     *
     * グラントの lifecycle state、取り消し、期限切れを AuthInfo に公開する
     * グラント状態へ畳み込む source of truth。
     */
    export const FromGrants = (params: { grants: Grant[]; now?: dayjs }): Props => {
      const { grants, now = dayjs(new Date()) } = params;
      const effective = grants.filter((grant) => IsCurrentlyEffective({ grant, now }));

      const durable = Key.Durable.filter((grantKey) => effective.some((grant) => grant.grantKey === grantKey));
      const ephemeral = effective
        .filter(
          (grant): grant is Grant & { grantKey: Key.Ephemeral; expiresAt: dayjs } => Key.IsEphemeral(grant.grantKey) && !!grant.expiresAt,
        )
        .map((grant) => ({
          id: grant.id,
          grantKey: grant.grantKey,
          expiresAt: grant.expiresAt,
          reason: grant.reason,
        }))
        .sort((a, b) => a.expiresAt.valueOf() - b.expiresAt.valueOf());

      return { durable, ephemeral };
    };

    export const HasDurable = (params: { grants: Props; grantKey: Key.Durable }): boolean =>
      params.grants.durable.includes(params.grantKey);

    export const HasEphemeral = (params: { grants: Props; grantKey: Key.Ephemeral }): boolean =>
      params.grants.ephemeral.some((grant) => grant.grantKey === params.grantKey);

    export const IsEmployee = (grants: Props): boolean => HasDurable({ grants, grantKey: Key.Employee });
    /** システム管理者権限の有効化を申請できる状態。アクティブなシステム管理者権限ではない。 */
    export const IsSystemAdminEligible = (grants: Props): boolean => HasDurable({ grants, grantKey: Key.SystemAdminEligible });
    /** アクティブなシステム管理者権限の source of truth。 */
    export const HasActiveSystemAdmin = (grants: Props): boolean => HasEphemeral({ grants, grantKey: Key.SystemAdminActive });
    /** PII アクセスの有効化を申請できる状態。アクティブな PII アクセスではない。 */
    export const IsPiiEligible = (grants: Props): boolean => HasDurable({ grants, grantKey: Key.PiiAccessEligible });
    /** アカウント単位の一時 PII グラントが有効かどうか。プロダクト別の PII 権限判定では、この値だけではなく productKey でスコープした Project.hasPiiAccessNow を使うこと。 */
    export const HasActivePiiAccess = (grants: Props): boolean => HasEphemeral({ grants, grantKey: Key.PiiAccessActive });
  }
}
